import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Save, Calendar, Wallet, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export function AdminPayoutsTab() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingSched, setSavingSched] = useState(false);
  const [savingPm, setSavingPm] = useState(false);
  const [schedule, setSchedule] = useState({
    cutoff_type: 'monthly_days',
    cutoff_days: [15, 30],
    cutoff_weekday: 4,
    cutoff_dates: [],
    cutoff_hour: 17,
    processing_delay_days: 3,
    timezone: 'Asia/Manila',
    is_enabled: true,
  });
  const [computed, setComputed] = useState(null);
  const [pm, setPm] = useState({
    api_key: '',
    api_key_masked: '',
    is_live: false,
    is_enabled: false,
    mock_mode: true,
    base_url: '',
    disburse_endpoint: '',
    org_id: '',
    method_map: null,
  });
  const [pmAdvanced, setPmAdvanced] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/config/payout-schedule'),
      api.get('/api/config/paymongo-payouts'),
    ]).then(([s, p]) => {
      const { computed: c, type, ...rest } = s.data || {};
      setSchedule((prev) => ({ ...prev, ...rest }));
      setComputed(c);
      setPm((prev) => ({ ...prev, ...(p.data || {}) }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [api]);

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      // Sanitize: ensure days are unique ints; only send relevant block per type
      const payload = {
        ...schedule,
        cutoff_days: (schedule.cutoff_days || []).map((d) => parseInt(d, 10)).filter((d) => d >= 1 && d <= 31),
        cutoff_dates: (schedule.cutoff_dates || []).filter(Boolean),
        cutoff_weekday: parseInt(schedule.cutoff_weekday, 10),
        cutoff_hour: parseInt(schedule.cutoff_hour, 10),
        processing_delay_days: parseInt(schedule.processing_delay_days, 10),
      };
      await api.put('/api/config/payout-schedule', payload);
      // Re-fetch to refresh computed
      const fresh = await api.get('/api/config/payout-schedule');
      setComputed(fresh.data?.computed || null);
      toast.success('Payout schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    }
    setSavingSched(false);
  };

  const savePm = async () => {
    setSavingPm(true);
    try {
      await api.put('/api/config/paymongo-payouts', {
        api_key: pm.api_key || '',
        is_live: !!pm.is_live,
        is_enabled: !!pm.is_enabled,
        mock_mode: !!pm.mock_mode,
      });
      toast.success('PayMongo Payouts settings saved');
      setPm((p) => ({ ...p, api_key: '' }));  // clear the input after save
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSavingPm(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* ===== Payout Schedule ===== */}
      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-[#00E5FF]" /> Payout Schedule & Cut-off</CardTitle>
          <CardDescription className="text-[#A0B4CC]">When are member withdrawals processed?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between p-3 rounded-md bg-[#000B18] border border-white/10">
            <div>
              <Label className="text-white">Enable scheduled payouts</Label>
              <p className="text-xs text-[#A0B4CC] mt-1">When disabled, requests stay pending until enabled.</p>
            </div>
            <Switch
              checked={schedule.is_enabled}
              onCheckedChange={(v) => setSchedule((s) => ({ ...s, is_enabled: v }))}
              data-testid="schedule-enabled-switch"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Cut-off type</Label>
              <Select
                value={schedule.cutoff_type}
                onValueChange={(v) => setSchedule((s) => ({ ...s, cutoff_type: v }))}
              >
                <SelectTrigger className="bg-[#000B18] border-white/20 text-white" data-testid="cutoff-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly_days">Monthly — specific days</SelectItem>
                  <SelectItem value="weekly">Weekly — one day per week</SelectItem>
                  <SelectItem value="custom">Custom — exact dates</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white mb-2 block">Processing delay (business days)</Label>
              <Input
                type="number" min="0" max="30"
                value={schedule.processing_delay_days}
                onChange={(e) => setSchedule((s) => ({ ...s, processing_delay_days: e.target.value }))}
                className="bg-[#000B18] border-white/20 text-white"
                data-testid="processing-delay-input"
              />
              <p className="text-xs text-[#A0B4CC] mt-1">e.g. cut-off Friday, delay 3 → payout next Wednesday.</p>
            </div>
          </div>

          {schedule.cutoff_type === 'monthly_days' && (
            <div>
              <Label className="text-white mb-2 block">Cut-off days of the month (comma-separated)</Label>
              <Input
                placeholder="15, 30"
                value={(schedule.cutoff_days || []).join(', ')}
                onChange={(e) => {
                  const parts = e.target.value.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
                  setSchedule((s) => ({ ...s, cutoff_days: parts }));
                }}
                className="bg-[#000B18] border-white/20 text-white"
                data-testid="cutoff-days-input"
              />
              <p className="text-xs text-[#A0B4CC] mt-1">Days 1-31. Day 31 in shorter months rolls to last day.</p>
            </div>
          )}

          {schedule.cutoff_type === 'weekly' && (
            <div>
              <Label className="text-white mb-2 block">Cut-off weekday</Label>
              <Select
                value={String(schedule.cutoff_weekday)}
                onValueChange={(v) => setSchedule((s) => ({ ...s, cutoff_weekday: parseInt(v, 10) }))}
              >
                <SelectTrigger className="bg-[#000B18] border-white/20 text-white" data-testid="cutoff-weekday-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                    <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {schedule.cutoff_type === 'custom' && (
            <div>
              <Label className="text-white mb-2 block">Cut-off dates (one per line, YYYY-MM-DD)</Label>
              <textarea
                rows={4}
                value={(schedule.cutoff_dates || []).join('\n')}
                onChange={(e) => setSchedule((s) => ({ ...s, cutoff_dates: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) }))}
                className="w-full p-2 rounded-md bg-[#000B18] border border-white/20 text-white font-mono text-sm"
                placeholder={'2026-03-15\n2026-03-30\n2026-04-15'}
                data-testid="cutoff-dates-textarea"
              />
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Cut-off hour (0-23)</Label>
              <Input
                type="number" min="0" max="23"
                value={schedule.cutoff_hour}
                onChange={(e) => setSchedule((s) => ({ ...s, cutoff_hour: e.target.value }))}
                className="bg-[#000B18] border-white/20 text-white"
              />
            </div>
            <div>
              <Label className="text-white mb-2 block">Timezone (display only)</Label>
              <Input
                value={schedule.timezone}
                onChange={(e) => setSchedule((s) => ({ ...s, timezone: e.target.value }))}
                className="bg-[#000B18] border-white/20 text-white"
                placeholder="Asia/Manila"
              />
            </div>
          </div>

          {computed && (
            <div className="rounded-md bg-gradient-to-r from-[#00E5FF]/10 to-[#7C4DFF]/10 border border-[#00E5FF]/20 p-3 flex items-start gap-2 text-sm">
              <Info className="w-4 h-4 text-[#00E5FF] mt-0.5" />
              <div className="text-[#A0B4CC]">
                <p><span className="text-white">Next cut-off:</span> {computed.next_cutoff}</p>
                <p><span className="text-white">Next payout:</span> {computed.next_payout}</p>
              </div>
            </div>
          )}

          <Button onClick={saveSchedule} disabled={savingSched} className="btn-primary" data-testid="save-schedule-btn">
            {savingSched ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Schedule
          </Button>
        </CardContent>
      </Card>

      {/* ===== PayMongo Payouts ===== */}
      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#7C4DFF]" /> PayMongo Disbursements
            <Badge className={`ml-2 ${pm.is_enabled && !pm.mock_mode ? 'bg-[#00E676]' : 'bg-[#FFEA00]/80 text-black'}`}>
              {pm.is_enabled && !pm.mock_mode ? 'LIVE' : 'MOCK'}
            </Badge>
          </CardTitle>
          <CardDescription className="text-[#A0B4CC]">
            Mocked by default. When PayMongo enables the Disbursements API for your merchant account,
            enter the key here and toggle off Mock Mode. The live API endpoint is already stubbed in
            <code className="text-[#00E5FF] mx-1">services/paymongo_payouts.py</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pm.api_key_masked && (
            <div className="text-sm text-[#A0B4CC] flex items-center justify-between gap-3">
              <span>Saved key: <code className="text-[#00E5FF]">{pm.api_key_masked}</code></span>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Clear the saved PayMongo Disbursements API key? Live disbursements will fail until a new key is entered.')) return;
                  setSavingPm(true);
                  try {
                    await api.put('/api/config/paymongo-payouts', {
                      api_key: '__CLEAR__',
                      is_live: pm.is_live,
                      is_enabled: pm.is_enabled,
                      mock_mode: pm.mock_mode,
                      base_url: pm.base_url || '',
                      disburse_endpoint: pm.disburse_endpoint || '',
                      org_id: pm.org_id || '',
                      method_map: pm.method_map || null,
                    });
                    toast.success('API key cleared');
                    setPm((p) => ({ ...p, api_key: '', api_key_masked: '' }));
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to clear key');
                  }
                  setSavingPm(false);
                }}
                className="text-xs text-[#FF1744] hover:underline"
                data-testid="clear-pm-key-btn"
              >
                Clear saved key
              </button>
            </div>
          )}
          <div>
            <Label className="text-white mb-2 block">PayMongo Disbursements Secret Key</Label>
            <Input
              type="password"
              placeholder={pm.api_key_masked ? 'Leave blank to keep saved key' : 'sk_live_… or sk_test_…'}
              value={pm.api_key}
              onChange={(e) => setPm((p) => ({ ...p, api_key: e.target.value }))}
              className="bg-[#000B18] border-white/20 text-white"
              data-testid="pm-api-key-input"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="flex items-center justify-between p-3 rounded-md bg-[#000B18] border border-white/10">
              <div>
                <Label className="text-white">Enabled</Label>
                <p className="text-xs text-[#A0B4CC] mt-0.5">Master switch</p>
              </div>
              <Switch checked={pm.is_enabled} onCheckedChange={(v) => setPm((p) => ({ ...p, is_enabled: v }))} data-testid="pm-enabled-switch" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-[#000B18] border border-white/10">
              <div>
                <Label className="text-white">Mock mode</Label>
                <p className="text-xs text-[#A0B4CC] mt-0.5">Simulate disbursements</p>
              </div>
              <Switch checked={pm.mock_mode} onCheckedChange={(v) => setPm((p) => ({ ...p, mock_mode: v }))} data-testid="pm-mock-switch" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md bg-[#000B18] border border-white/10">
              <div>
                <Label className="text-white">Live</Label>
                <p className="text-xs text-[#A0B4CC] mt-0.5">vs. sandbox</p>
              </div>
              <Switch checked={pm.is_live} onCheckedChange={(v) => setPm((p) => ({ ...p, is_live: v }))} data-testid="pm-live-switch" />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPmAdvanced((v) => !v)}
            className="text-xs text-[#00E5FF] hover:underline self-start"
            data-testid="toggle-pm-advanced"
          >
            {pmAdvanced ? '▾ Hide advanced overrides' : '▸ Advanced overrides (only if PayMongo schema differs)'}
          </button>
          {pmAdvanced && (
            <div className="space-y-3 p-3 rounded-md bg-[#000B18] border border-white/10">
              <p className="text-xs text-[#A0B4CC]">
                Defaults: <code className="text-[#00E5FF]">https://api.paymongo.com/v1</code> + <code className="text-[#00E5FF]">/disbursements</code>. Leave blank to use defaults. Adjust here if PayMongo&apos;s published Disbursements API path or field names differ from our integration assumptions.
              </p>
              <div>
                <Label className="text-white mb-1 block text-xs">Base URL override</Label>
                <Input value={pm.base_url || ''} onChange={(e) => setPm((p) => ({ ...p, base_url: e.target.value }))} placeholder="https://api.paymongo.com/v1" className="bg-[#041A3B] border-white/20 text-white text-sm" data-testid="pm-base-url" />
              </div>
              <div>
                <Label className="text-white mb-1 block text-xs">Disbursement endpoint path</Label>
                <Input value={pm.disburse_endpoint || ''} onChange={(e) => setPm((p) => ({ ...p, disburse_endpoint: e.target.value }))} placeholder="/disbursements" className="bg-[#041A3B] border-white/20 text-white text-sm" data-testid="pm-disburse-endpoint" />
              </div>
              <div>
                <Label className="text-white mb-1 block text-xs">Organization-Id header (Money Movement)</Label>
                <Input value={pm.org_id || ''} onChange={(e) => setPm((p) => ({ ...p, org_id: e.target.value }))} placeholder="org_xxxxx" className="bg-[#041A3B] border-white/20 text-white text-sm" data-testid="pm-org-id" />
              </div>
              <div>
                <Label className="text-white mb-1 block text-xs">Method map (JSON)</Label>
                <Input
                  value={pm.method_map ? JSON.stringify(pm.method_map) : ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (!v) { setPm((p) => ({ ...p, method_map: null })); return; }
                    try { setPm((p) => ({ ...p, method_map: JSON.parse(v) })); } catch { /* keep typing */ }
                  }}
                  placeholder='{"gcash":"gcash","paymaya":"maya","bank":"instapay"}'
                  className="bg-[#041A3B] border-white/20 text-white text-sm font-mono"
                  data-testid="pm-method-map"
                />
                <p className="text-[10px] text-[#5C7599] mt-1">Maps our internal method names to PayMongo&apos;s canonical payout method strings.</p>
              </div>
            </div>
          )}
          <Button onClick={savePm} disabled={savingPm} className="btn-primary" data-testid="save-pm-btn">
            {savingPm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save PayMongo Settings
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
